import {FC, useEffect, useMemo} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {chargersApi, employeeApi, usersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {ScheduleFormType} from 'src/components/forms/formData/schedule';
import {ScheduleForm} from 'src/components/forms/ScheduleForm';
import {ScheduleUserOption} from 'src/components/forms/ScheduleForm/ScheduleForm.types';
import {ScheduleTypeEnum} from 'src/enums';
import {DayOfWeek} from 'src/types/pricing';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {ChargerScheduleCreateWrapperType} from './ChargerScheduleCreateWrapper.types';

type AddScheduleMutationTrigger = ReturnType<typeof chargersApi.useAddScheduleMutation>[0];

const handleSubmit =
    (
        trigger: AddScheduleMutationTrigger,
        dispatch: AppDispatch,
        businessId: number,
        chargerId: number,
        handleClose: () => void,
        companyId?: number
    ) =>
        async (values: ScheduleFormType) => {
            const data = {
                businessId,
                userId: Number(values.userId),
                companyId,
                startDay: values.startDay as DayOfWeek,
                startHour: Number(values.startHour),
                startMinute: Number(values.startMinute),
                stopDay: values.stopDay as DayOfWeek,
                stopHour: Number(values.stopHour),
                stopMinute: Number(values.stopMinute),
                type: values.type ? ScheduleTypeEnum.CYCLE : ScheduleTypeEnum.ONE_TIME,
            };
            dispatch(preloaderChangeStatus(true));
            try {
                await trigger({businessId, chargerId, data}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                handleClose();
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ChargerScheduleCreateWrapper: FC<ChargerScheduleCreateWrapperType> = ({
                                                                                       businessId,
                                                                                       handleClose,
                                                                                       chargerId,
                                                                                       chargerData,
                                                                                   }) => {
    const [trigger] = chargersApi.useAddScheduleMutation();
    const dispatch = useAppDispatch();
    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);

    // Check if current user is EMPLOYEE
    const isEmployeeUser = persona.isEmployee;
    // Determine which users to fetch based on charger data
    const hasOwner = !!chargerData.user;
    const hasCompany = !!chargerData.company;
    const companyId = chargerData.company?.id;

    // Fetch employees if charger has company
    const [fetchEmployees, employeesResult] = employeeApi.useLazySearchEmployeesQuery();

    // Fetch all users if charger has no owner and no company
    const [fetchUsers, usersResult] = usersApi.useLazyGetUsersQuery();

    useEffect(() => {
        // EMPLOYEE users can only create schedules for themselves - no need to fetch
        if (isEmployeeUser) {
            return;
        }

        if (hasOwner) {
            // Owner exists - no need to fetch, we already have the user
            return;
        }

        if (hasCompany && companyId) {
            // Fetch employees of the company
            fetchEmployees({
                params: {page: 0, size: 1000},
                data: {businessId, companyId},
            });
        } else {
            // Fetch all users of the business
            fetchUsers({
                params: {page: 0, size: 1000},
                data: {businessId},
            });
        }
    }, [hasOwner, hasCompany, companyId, businessId, isEmployeeUser]);

    // Build users list based on charger data
    const users: ScheduleUserOption[] = useMemo(() => {
        // Case 0: EMPLOYEE user - only show themselves
        if (isEmployeeUser && authInfo) {
            return [{
                id: authInfo.id,
                name: `${authInfo.firstName || ''} ${authInfo.lastName || ''}`.trim() || authInfo.emailAddress || '',
                value: authInfo.id,
            }];
        }

        // Case 1: Charger has an owner (private driver)
        if (hasOwner && chargerData.user) {
            return [{
                id: chargerData.user.id,
                name: `${chargerData.user.firstName} ${chargerData.user.lastName}`,
                value: chargerData.user.id,
            }];
        }

        // Case 2: Charger has a company - use employees
        if (hasCompany && employeesResult.data?.content) {
            return employeesResult.data.content.map((employee) => ({
                id: employee.user.id,
                name: `${employee.firstName} ${employee.lastName}`,
                value: employee.user.id,
            }));
        }

        // Case 3: No owner and no company - use all business users
        if (!hasOwner && !hasCompany && usersResult.data?.content) {
            return usersResult.data.content.map((user) => ({
                id: user.id,
                name: `${user.firstName} ${user.lastName}`,
                value: user.id,
            }));
        }

        return [];
    }, [hasOwner, hasCompany, chargerData.user, employeesResult.data, usersResult.data, isEmployeeUser, authInfo]);

    const isLoadingUsers = !isEmployeeUser && (employeesResult.isLoading || usersResult.isLoading);

    return (
        <ScheduleForm
            handleClose={handleClose}
            handleSubmit={handleSubmit(trigger, dispatch, businessId, chargerId, handleClose, companyId)}
            users={users}
            isLoadingUsers={isLoadingUsers}
        />
    );
};
